import React, { useState } from "react";
import { Plus } from "lucide-react";
import Achievement, { AchievementData } from "./Achievement";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Task, StarHistoryEntry } from "@/utils/storage";
import { DeleteConfirmationDialog } from "../dialogs";

interface TrophyCaseProps {
  achievements: AchievementData[];
  totalStars: number;
  editMode?: boolean;
  tasks?: Task[];
  starHistory?: StarHistoryEntry[];
  totalStarsEarned?: number;
  onEdit?: (achievement: AchievementData) => void;
  onDelete?: (id: string) => void;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
  onAddNew?: () => void;
}

const TrophyCase: React.FC<TrophyCaseProps> = ({
  achievements,
  totalStars,
  editMode = false,
  tasks = [],
  starHistory: _starHistory = [],
  totalStarsEarned: _totalStarsEarned = 0,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onAddNew,
}) => {
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [achievementToDelete, setAchievementToDelete] =
    useState<AchievementData | null>(null);

  // Determine sorting strategy
  let sortedAchievements: AchievementData[];

  // In edit mode, prioritize manual order if any achievement has an order property
  const hasManualOrder = achievements.some((a) => a.order !== undefined);

  if (editMode && hasManualOrder) {
    // Sort by order property in edit mode, putting items without order at the end
    sortedAchievements = [...achievements].sort((a, b) => {
      const orderA = a.order ?? 999999;
      const orderB = b.order ?? 999999;
      return orderA - orderB;
    });
  } else {
    // Use the original categorized sorting logic
    // Separate unlocked and locked achievements
    const unlockedAchievements = achievements.filter((a) => a.unlocked);
    const lockedAchievements = achievements.filter((a) => !a.unlocked);

    // Separate task-based and star-based locked achievements
    const taskBasedAchievements = lockedAchievements.filter(
      (a) => a.linkedTaskId
    );
    const starBasedAchievements = lockedAchievements.filter(
      (a) => !a.linkedTaskId
    );

    // Sort unlocked by date (newest first)
    unlockedAchievements.sort((a, b) => {
      if (!a.date || !b.date) return 0;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    // Sort star-based by required stars (lowest first)
    starBasedAchievements.sort((a, b) => a.requiredStars - b.requiredStars);

    // Sort task-based by required completions (lowest first)
    taskBasedAchievements.sort((a, b) => {
      if (a.requiredCompletions && b.requiredCompletions) {
        return a.requiredCompletions - b.requiredCompletions;
      }
      return 0;
    });

    // Combine all lists in the desired order
    sortedAchievements = [
      ...unlockedAchievements,
      ...taskBasedAchievements,
      ...starBasedAchievements,
    ];
  }

  const handleDeleteClick = (achievement: AchievementData) => {
    setAchievementToDelete(achievement);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (achievementToDelete && onDelete) {
      onDelete(achievementToDelete.id);
    }
    setDeleteDialogOpen(false);
    setAchievementToDelete(null);
  };

  // Calculate unlocked achievements
  const unlockedCount = achievements.filter((a) => a.unlocked).length;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header with achievement progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">🏆 Trophy Case</div>
            <div className="flex items-center gap-4">
              <div className="text-sm font-normal text-muted-foreground">
                {unlockedCount}/{achievements.length}
              </div>
              {editMode && onAddNew && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onAddNew}
                  className="flex items-center gap-1"
                >
                  <Plus size={16} />
                  Add Trophy
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sortedAchievements.length === 0 ? (
          <div className="col-span-full text-center p-8 glassmorphism rounded-2xl">
            <p className="text-muted-foreground">No achievements yet 😢</p>
            <p className="text-sm mt-2">
              Keep collecting stars to unlock achievements!
            </p>
            {editMode && onAddNew && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAddNew}
                className="mt-4"
              >
                Add your first trophy
              </Button>
            )}
          </div>
        ) : (
          sortedAchievements.map((achievement) => (
            <Achievement
              key={achievement.id}
              achievement={achievement}
              totalStars={totalStars}
              editMode={editMode}
              tasks={tasks}
              onEdit={onEdit ? () => onEdit(achievement) : undefined}
              onDelete={
                onDelete ? () => handleDeleteClick(achievement) : undefined
              }
              onMoveUp={onMoveUp ? () => onMoveUp(achievement.id) : undefined}
              onMoveDown={
                onMoveDown ? () => onMoveDown(achievement.id) : undefined
              }
            />
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        itemType="achievement"
        itemName={achievementToDelete?.title}
      />
    </div>
  );
};

export default TrophyCase;
